/* eslint-disable no-undef */
import { Coordinates } from '@volkswagen-onehub/audi-feature-service-visitor-locator';
import { WindowSize } from '../models/common/windowSize';
import { regionDetails } from './geocodeDetails';

export interface Boundary {
  ne: Coordinates;
  sw: Coordinates;
}

export async function findFirstCountry(
  country: string,
): Promise<google.maps.GeocoderResult | undefined> {
  const resultList: google.maps.GeocoderResult[] = await regionDetails(country);
  return resultList.find((geocoderResult: google.maps.GeocoderResult) =>
    geocoderResult.types.includes('country'),
  );
}

export function calculateZoomLevelWithGeocoderResult(
  geocoderResult: google.maps.GeocoderResult,
  windowSize: WindowSize,
): number {
  return calculateZoomLevelWithBoundary(
    {
      ne: {
        lat: geocoderResult.geometry.bounds.getNorthEast().lat(),
        lng: geocoderResult.geometry.bounds.getNorthEast().lng(),
      },
      sw: {
        lat: geocoderResult.geometry.bounds.getSouthWest().lat(),
        lng: geocoderResult.geometry.bounds.getSouthWest().lng(),
      },
    },
    windowSize,
    true,
  );
}

export function calculateZoomLevelWithBoundary(
  boundary: Boundary,
  { height, width }: WindowSize,
  calculationForCountry: boolean,
): number {
  const WORLD_DIM = { height: 256, width: 256 };
  const ZOOM_MAX = 21;

  function latRad(lat: number) {
    const sin = Math.sin((lat * Math.PI) / 180);
    const radX2 = Math.log((1 + sin) / (1 - sin)) / 2;
    return Math.max(Math.min(radX2, Math.PI), -Math.PI) / 2;
  }

  function zoom(mapPx: number, worldPx: number, fraction: number) {
    return Math.floor(Math.log(mapPx / worldPx / fraction) / Math.LN2);
  }

  const { ne, sw } = boundary;

  const latFraction = (latRad(ne.lat) - latRad(sw.lat)) / Math.PI;

  const lngDiff = ne.lng - sw.lng;
  const lngFraction = (lngDiff < 0 ? lngDiff + 360 : lngDiff) / 360;

  const latZoom = zoom(height, WORLD_DIM.height, latFraction);
  const lngZoom = zoom(width, WORLD_DIM.width, lngFraction);

  return Math.min(latZoom, lngZoom, ZOOM_MAX) - (calculationForCountry ? 0 : 1);
}
